import { useEffect, useState, useContext, useRef } from "react";
import { CircularProgress } from "@mui/material";

import MUICard from "./../../../Component/UI/MUICard/MUICard";
import classes from "./History.module.css";
import HistoryItem from "./HistoryItem";
import LoadingSkeleton from "./LoadingSkeleton";
import EmptyView from "./../../../Component/EmptyView/EmptyView";
import { AuthContext } from "../../../context/auth-context";
import useHistory from "./useHistory";
import MUIButton from "./../../../Component/UI/MUIButton/MUIButton";
import ErrorView from "./../../../Component/ErrorView/ErrorView";
import ErrorMessage from "./../../../Component/ErrorMessage/ErrorMessage";
import { DiscoveryContext } from "../discovery-context";
import { sanitizeObject } from "./../../../utils/helpers";

function hasData(obj, type) {
  const baseExpectedTypes = {
    name: "string",
    llm: "string",
    type: "string",
    competitors: "object",
    brand_scores: "object",
    category_dimensions: "object",
  };

  let expectedTypes = { ...baseExpectedTypes };

  if (type === "brand") {
    expectedTypes.core_attributes = "object";
    // expectedTypes.top_products = "object";
    expectedTypes.brand_description = "string";
    expectedTypes.brand_snippets = "object";
    expectedTypes.brand_themes = "object";
    expectedTypes.entity = "string";
    expectedTypes.positive_negative_attributes = "object";
    // expectedTypes.sources=  "object";
  } else if (type === "product") {
    expectedTypes.taxonomies = "object";
    expectedTypes.brand_description = "string";
    expectedTypes.brand_snippets = "object";
    expectedTypes.brand_themes = "object";
    expectedTypes.entity = "string";
    expectedTypes.positive_negative_attributes = "object";
    // expectedTypes.sources=  "object";
  } else if (type === "category") {
    expectedTypes.taxonomies = "object";
  }

  for (const key in expectedTypes) {
    if (expectedTypes.hasOwnProperty(key)) {
      const expectedType = expectedTypes[key];
      const actualValue = obj[key];

      // Check if the property exists in the object
      if (!(key in obj)) {
        return false;
      }

      const actualType = Array.isArray(actualValue)
        ? "object"
        : typeof actualValue;

      if (actualType !== expectedType) {
        return false;
      }

      if (expectedType === "object" && Array.isArray(actualValue)) {
        if (
          actualValue.length === 0 &&
          key !== "category_dimensions" &&
          key !== "core_attributes" &&
          key !== "positive_negative_attributes" &&
          key !== "brand_themes"
        ) {
          return false;
        }
        if (key === "brand_scores") {
          if (
            type === "category"
              ? actualValue.length === 0
              : actualValue.length !== 4 ||
                actualValue.some(
                  (item) =>
                    item === null ||
                    item === undefined ||
                    !item.hasOwnProperty("total") ||
                    item.hasOwnProperty("error")
                )
          ) {
            return false;
          }
        }
      } else if (expectedType === "object" && !Array.isArray(actualValue)) {
        // Check if the object is empty for specific keys
        if (
          (key === "category_dimensions" ||
            key === "core_attributes" ||
            key === "positive_negative_attributes" ||
            key === "brand_themes") &&
          Object.keys(actualValue).length === 0
        ) {
          return false;
        }
      } else if (expectedType === "string" && actualValue.trim() === "") {
        return false;
      }
    }
  }
  return true;
}

const PAGE_LIMIT = 5;

export default function History({ filter = "brand" }) {
  const [filterOption, setFilterOption] = useState(filter);
  const { authUserEmail } = useContext(AuthContext);
  const discoveryCtx = useContext(DiscoveryContext);
  const [page, setPage] = useState(1);
  const {
    history,
    addHistory,
    loading,
    getHistory,
    hasReachedEnd,
    deleteHistory,
    updateHistory,
    error,
  } = useHistory(authUserEmail, filterOption);
  const historyRef = useRef(false);

  const state = discoveryCtx.state;

  useEffect(() => {
    setFilterOption(filter);
    setPage(1);
  }, [filter]);

  useEffect(() => {
    getHistory(page, PAGE_LIMIT);
  }, [page, filterOption]);

  function deleteHistoryItemHandler(id) {
    discoveryCtx.resetState();
    deleteHistory(id);
    if(history.length === 1) {
      setPage(page=> page + 1);
    }
  }

  useEffect(() => {
    const type = state.type ?? "brand";
    if (hasData(state, type)) {
      if (!state.id && type !== "category" && !historyRef.current) {
        console.log("LOG ✅ GOT DATA", state);
        const sanitizedData = sanitizeObject(state);
        addHistory({
          user_email: authUserEmail,
          name: `${state.entity} ${state?.type} Overview`,
          date: Date.now(),
          category: state.category,
          llm: state.llm,
          type: state.type,
          entity: state.entity,
          json_data: sanitizedData,
        });
        discoveryCtx.updateLoading(false);
        historyRef.current = true;
      }
    } else {
      if (state.id) {
        discoveryCtx.updateLoading(false);
      } else if (state.is_launched) {
        discoveryCtx.updateLoading(true);
      }
    }
  }, [hasData(state, state.type)]);

  useEffect(() => {
    const stateId = state?.id?.toString() || "";
    if (!stateId.trim()) {
      historyRef.current = false;
    }
  }, [state?.id]);

  return (
    <MUICard variant="elevated" elevation={0} className={classes.card}>
      <div className={classes.header}>
        <span className={classes.title}>History</span>
      </div>
      {history.length > 0 && (
        <div className={classes.container}>
          {history.map((historyItem) => (
            <HistoryItem
              key={historyItem.id}
              item={historyItem}
              onDelete={deleteHistoryItemHandler}
              onUpdate={(id, name) => updateHistory(id, name)}
              isOldHistory={false}
            />
          ))}
          <div className={classes.load_more}>
            {error && history.length > 0 && (
              <div className={classes.error_message}>
                <ErrorMessage message={error} />
              </div>
            )}
            {!loading && !hasReachedEnd && !discoveryCtx.loading && (
              <MUIButton
                className={classes.load_btn}
                variant="text"
                onClick={() => setPage((page) => page + 1)}
              >
                Load More
              </MUIButton>
            )}
            {loading && <CircularProgress size={22} />}
          </div>
        </div>
      )}
      {loading && history.length === 0 && <LoadingSkeleton />}
      {!loading && !error && history.length === 0 && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "70%" }}
        >
          <EmptyView message={"Nothing here."} />
        </div>
      )}
      {error && history.length === 0 && (
        <div className={classes.error}>
          <ErrorView title="Error" message={error} />
        </div>
      )}
    </MUICard>
  );
}
